import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaTicketAlt } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';
import UiModal from '../components/UiModal';

const FIELDS = [
  { label: 'Name', name: 'name' },
  { label: 'Description', name: 'description' },
  { label: 'Code', name: 'code' },
  { label: 'Min Order Value', name: 'min_order_value', type: 'number' },
  { label: 'Max Order Value', name: 'max_order_value', type: 'number' },
  { label: 'Offer %', name: 'offer_percentage', type: 'number' },
  { label: 'Expiry Date', name: 'expiry_date', type: 'date' },
];

function initialFormState() {
  return { name: '', description: '', code: '', min_order_value: '', max_order_value: '', offer_percentage: '', expiry_date: '' };
}

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialFormState());

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/coupons`);
      setCoupons(res.data || []);
    } catch (err) {
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const resetForm = () => { setForm(initialFormState()); setIsEditMode(false); setEditId(null); };

  const handleSubmit = async () => {
    try {
      if (isEditMode) await axios.put(`${API_BASE}/api/coupons/${editId}`, form);
      else await axios.post(`${API_BASE}/api/coupons`, form);
      fetchCoupons();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving coupon:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/coupons/${id}`);
      if (res.data.success) setCoupons((prev) => prev.filter((c) => c.id !== id));
      else alert(res.data.message);
    } catch (err) {
      console.error('Error deleting coupon:', err);
    }
  };

  const handleEdit = (coupon) => {
    setForm({ ...coupon, expiry_date: coupon.expiry_date ? coupon.expiry_date.split('T')[0] : '' });
    setEditId(coupon.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Coupons</h2>
          <p className="text-sm text-slate-500">Discount codes available to customers.</p>
        </div>
        <button className="ui-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <FaPlus className="text-xs" /> Add coupon
        </button>
      </div>

      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Offer</th>
                <th>Min Order</th>
                <th>Max Order</th>
                <th>Expiry</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[100px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!loading && coupons.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaTicketAlt className="text-2xl" />
                      <p className="text-sm">No coupons yet.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && coupons.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium text-slate-900">{c.name}</td>
                  <td><span className="ui-badge bg-indigo-50 font-mono text-indigo-700">{c.code}</span></td>
                  <td className="font-medium text-slate-900">{c.offer_percentage}%</td>
                  <td className="text-slate-500">{c.min_order_value}</td>
                  <td className="text-slate-500">{c.max_order_value}</td>
                  <td className="whitespace-nowrap text-slate-500">
                    {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => handleEdit(c)}>
                        <FaEdit className="text-xs" /> Edit
                      </button>
                      <button
                        className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(c.id)}
                      >
                        <FaTrash className="text-xs" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UiModal
        open={showModal}
        title={isEditMode ? 'Edit Coupon' : 'Add Coupon'}
        onClose={() => setShowModal(false)}
        footer={
          <>
            <button className="ui-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="ui-btn-primary" onClick={handleSubmit}>{isEditMode ? 'Update' : 'Save'}</button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(({ label, name, type = 'text' }) => (
            <div key={name} className={name === 'description' ? 'sm:col-span-2' : ''}>
              <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
              <input className="ui-input" type={type} name={name} value={form[name] ?? ''} onChange={handleChange} required />
            </div>
          ))}
        </div>
      </UiModal>
    </div>
  );
};

export default Coupons;
